import FileInput from "Common/fileInput";
import useInputChange from "hooks/useInputChange";

import { FormEvent, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { ToastContainer } from "react-toastify";
import { updateAgency } from "services/user.service";

import {
  Agency,
  AgencyCompanyInfo,
  AgencyPersonalDetails,
} from "types/agency.interface";

import { GENDER, RELIGION } from "types/verification.type";

interface Props {
  onSubmit?: () => void;
  data: Agency | null;
  onCancel: () => void;
}
export default function AgencyForm({ onSubmit, data, onCancel }: Props) {
  const {
    state: input,
    onChangeByNameValue,
    onChange,
    setState,
  } = useInputChange<Partial<AgencyCompanyInfo & AgencyPersonalDetails>>(
    data || {
      state: "",
      lga: "",
      utilityBill: null,
      dob: "",
      street: "",
      area: "",
      name: "",
      gender: "",
      religion: "",
      profilePicture: null,
      nin: null,
      companyEmail: "",
      officeAddress: "",
      phoneNumber: "",
      services: [],
      about: "",

      cac: null,
    }
  );

  const [errorMessage, setErrorMessage] = useState("");
  const queryClient = useQueryClient();
  useEffect(() => {
    if (data) {
      setState(data);
    }
  }, [data]);

  const { isLoading, mutate } = useMutation(
    async (cred: AgencyCompanyInfo & AgencyPersonalDetails) =>
      updateAgency(data?._id!, cred),
    {
      onSuccess(res) {
        queryClient.invalidateQueries(["profile", data?.user!]);
        alert("Update successful");
        if (onSubmit) onSubmit();
      },
      onError(error: any) {
        setErrorMessage(error.response.data.message);
      },
    }
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    const payload: AgencyPersonalDetails & AgencyCompanyInfo = {
      ...data!,
      ...input,
    };

    mutate(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <ToastContainer closeButton={false} limit={1} />

      <div className="flex flex-col gap-2">
        <label htmlFor="name">Agency name</label>
        <input
          value={input.name}
          required
          onChange={onChange}
          placeholder="name"
          id="name"
          name="name"
          className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="name">About</label>
        <textarea
          value={input.about}
          required
          onChange={onChange}
          placeholder="about"
          id="about"
          name="about"
          className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="companyEmail">Company email</label>
        <input
          value={input.companyEmail}
          required
          onChange={onChange}
          placeholder="company email"
          id="companyEmail"
          name="companyEmail"
          className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="street">Street</label>
        <input
          value={input.street}
          required
          onChange={onChange}
          placeholder="Street"
          id="street"
          name="street"
          className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="area">Area</label>
        <input
          value={input.area}
          required
          onChange={onChange}
          placeholder="Area"
          id="area"
          name="area"
          className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="dob">Date of Birth</label>
        <input
          value={input.dob}
          required
          type="date"
          onChange={onChange}
          placeholder="dob"
          id="dob"
          name="dob"
          className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex flex-col gap-2 ">
        <label htmlFor="religion">Religion</label>
        <select
          id="religion"
          name="religion"
          value={input.religion}
          onChange={onChange}
          className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select....</option>
          {RELIGION.map((item: any, i: any) => {
            return (
              <option key={i} value={item.value}>
                {item.label}
              </option>
            );
          })}
        </select>
      </div>

      <div className="flex flex-col gap-2 ">
        <label htmlFor="gender">Gender</label>
        <select
          id="gender"
          name="gender"
          value={input.gender}
          onChange={onChange}
          className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select....</option>
          {GENDER.map((item: any, i: any) => {
            return (
              <option key={i} value={item.value}>
                {item.label}
              </option>
            );
          })}
        </select>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="address" className="text-sm font-medium text-gray-700">
          Office Address
        </label>

        <textarea
          name="address"
          id="address"
          value={input.address}
          onChange={onChange}
          placeholder="Some address"
          className="w-full border border-gray-300 bg-input px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div className="mb-3">
        <label className="inline-block mb-2 text-base font-medium">
          Profile Photo
        </label>

        <FileInput
          file={input.profilePicture!}
          label="Profile photo"
          onUpload={(file) => onChangeByNameValue("profilePicture", file)}
          isrequired={true}
        />
      </div>

      {/* <div className="mb-3">
        <label className="inline-block mb-2 text-base font-medium">
          Upload CAC Document
        </label>

        <FileInput
          file={input.cac!}
          label="Upload Identity Document"
          onUpload={(file) => onChangeByNameValue("cac", file)}
          isrequired
        />
      </div> */}
      <div className="mb-3">
        <label className="inline-block mb-2 text-base font-medium">
          Utility bill
        </label>

        <FileInput
          file={input.utilityBill!}
          label="Upload utility bill"
          onUpload={(file) => onChangeByNameValue("utilityBill", file)}
          isrequired
        />
      </div>

      {errorMessage && (
        <span className=" text-red-500 text-sm my-4">{errorMessage}</span>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="reset"
          data-modal-close="addDocuments"
          className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
        >
          {isLoading ? "Loading..." : "Submit"}
        </button>
      </div>
    </form>
  );
}
